import React, { Component, Fragment } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import { get, post } from 'axios'
import uniq from 'lodash/uniq'

import EventsFilters from './components/EventsFilters'
import { ArtistType, CategoryType, EventType, VenueGroupType, VenueType } from './types'
import { EventsSchedule } from './EventsSchedule'

export const EventsIndexProps = {
  baseUrl: PropTypes.string.isRequired,
  events: PropTypes.arrayOf(EventType),
  artists: PropTypes.arrayOf(ArtistType),
  days: PropTypes.arrayOf(PropTypes.string),
  experiences: PropTypes.arrayOf(CategoryType),
  categories: PropTypes.arrayOf(CategoryType),
  accessibilities: PropTypes.arrayOf(CategoryType),
  venueGroups: PropTypes.arrayOf(VenueGroupType),
  venues: PropTypes.arrayOf(VenueType),
  filters: PropTypes.shape({
    view: PropTypes.oneOf(['catalogue', 'schedule']),
    day: PropTypes.arrayOf(PropTypes.string),
    artist: PropTypes.arrayOf(PropTypes.number),
    venue: PropTypes.arrayOf(PropTypes.number),
    lat: PropTypes.string,
    lng: PropTypes.string,
    radius: PropTypes.number,
    category: PropTypes.arrayOf(PropTypes.number),
    a11y: PropTypes.arrayOf(PropTypes.number),
    free: PropTypes.oneOf([1, null]),
    online: PropTypes.oneOf([1, null]),
    location: PropTypes.string,
  }),
  sortby: PropTypes.string,
}

const PARAM_CONFIG = {
  day: { type: String, multiple: true },
  period: { type: String },
  artist: { type: Number },
  venue: { type: Number, multiple: true },
  region: { type: Number, multiple: true },
  experience: { type: Number, multiple: true },
  category: { type: Number, multiple: true },
  a11y: { type: Number, multiple: true },
  location: { type: String },
  lat: { type: Number },
  lng: { type: Number },
  radius: { type: Number },
  free: { type: Number },
  highlight: { type: Number },
  online: { type: Number },
  view: { type: String, isFilter: false },
  sortby: { type: String, isFilter: false },
}

function parseQueryString(stringParams = window.location.search) {
  return queryString.parse(stringParams, { arrayFormat: 'bracket' })
}

function coerceParamValues(paramValues = {}, paramConfig = PARAM_CONFIG) {
  const values = {}

  Object.entries(paramConfig).forEach(([key, config]) => {
    if (config.default) {
      values[key] = config.default
    } else if (config.multiple) {
      values[key] = []
    } else {
      values[key] = null
    }
    const value = paramValues[key]
    if (value) {
      if (!Array.isArray(value) && config.multiple) {
        values[key] = [config.type(value)]
      } else if (Array.isArray(value) && !config.multiple) {
        values[key] = config.type(value[0])
      } else if (Array.isArray(value)) {
        values[key] = value.map(config.type)
      } else {
        values[key] = config.type(value)
      }
    }
  })

  return values
}

function isFiltered(filterValues, filterDefinitions = PARAM_CONFIG) {
  for (let key in filterValues) {
    if (filterDefinitions[key].isFilter === false) continue
    if (Array.isArray(filterValues[key]) && !filterValues[key].length) continue
    if (filterValues[key]) return true
  }
  return false
}

export default class EventsIndex extends Component {
  static propTypes = EventsIndexProps
  static defaultProps = {
    filters: {},
    events: [],
    categories: [],
    accessibilities: [],
    venueGroups: [],
    venues: [],
    artists: [],
    days: [],
  }

  constructor(props) {
    super(props)
    console.log('EventsIndex', props)
    this.dataCache = {}
    this.searchTimeout = null
    const filters = coerceParamValues(parseQueryString())
    this.state = {
      showFilters: isFiltered(filters),
      filters,
      loading: false,
      errorLoading: false,
      numResults: props.numResults !== undefined ? props.numResults : this.getNumResultsFromHtml(),
      events: props.events || [],
      days: props.events ? this.getDaysFromData(props.events) : props.days,
    }
    window.updateEventFilter = this.updateFilters
  }

  componentWillUnmount() {
    if (window.updateEventFilter === this.updateFilters) window.updateEventFilter = null
  }

  getNumResultsFromHtml() {
    const el = document.querySelector('[data-id="child-events-number"]')
    if (el) {
      return parseInt(el.dataset.events, 10)
    }
    return 0
  }

  getNumResultsFromData(events) {
    return events.reduce((acc, childEvents) => acc + (childEvents.length || 1), 0)
  }

  getDaysFromData(events, buffer = 1) {
    let allDays = this.props.days || []
    let days = [...allDays]

    const allEventDays = events
      .flatMap(event => [event.start_date, ...event.sessions.map(session => session.datetime)])
      .filter(Boolean)
      .map(str => str.split('T')[0])
      .sort()

    // fill in any blanks that aren't in props.days for whatever reason
    allDays = uniq([...allDays, ...allEventDays]).sort()

    const firstDay = allEventDays[0]
    const lastDay = allEventDays[allEventDays.length - 1]
    const firstDayIndex = Math.max(0, allDays.indexOf(firstDay) - buffer)
    const lastDayIndex = Math.min(allDays.indexOf(lastDay) + buffer + 1, allDays.length)
    days = allDays.slice(firstDayIndex, lastDayIndex)
    // console.log({ allDays, allEventDays, firstDay, lastDay, firstDayIndex, lastDayIndex, days })

    return days
  }

  loadEvents() {
    if (this.state.filters.view === 'schedule') {
      this.loadSchedule()
    } else {
      this.loadEventsHtml()
    }
  }

  loadEventsHtml() {
    const { baseUrl } = this.props
    const url = `${window.location.origin}${baseUrl}${window.location.search}`
    if (url in this.dataCache) {
      const data = this.dataCache[url]
      this.updateEventsHtml(data)
      this.setState({ numResults: !data ? 0 : this.getNumResultsFromHtml(), data: [] })
    } else {
      this.setState({ loading: true, errorLoading: false })
      post(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(({ data }) => {
          this.dataCache[url] = data
          this.updateEventsHtml(data)
          this.setState({
            loading: false,
            errorLoading: false,
            numResults: !data ? 0 : this.getNumResultsFromHtml(),
            data: [],
          })
        })
        .catch(e => this.setState({ loading: false, errorLoading: true }))
    }
  }

  loadSchedule() {
    const { baseUrl } = this.props
    const url = `${window.location.origin}${baseUrl}.json${window.location.search}`
    if (url in this.dataCache) {
      const events = this.dataCache[url]
      this.setState({
        numResults: this.getNumResultsFromData(events),
        days: this.getDaysFromData(events),
        events,
      })
    } else {
      this.setState({ loading: true, errorLoading: false })
      post(url)
        .then(({ data }) => {
          const events = data.data
          this.dataCache[url] = events
          this.updateEventsHtml('')
          this.setState({
            loading: false,
            errorLoading: false,
            numResults: this.getNumResultsFromData(events),
            days: this.getDaysFromData(events),
            events,
          })
        })
        .catch(e => this.setState({ loading: false, errorLoading: true }))
    }
  }

  updateEventsHtml(html) {
    if (!html) {
      window.jQuery('[data-ajax-form-target="events"]').empty()
    } else if (window.replaceAjaxFormTargets) {
      window.replaceAjaxFormTargets(html)
    }
  }

  updateUrl() {
    const { filters } = this.state
    const urlParams = queryString.stringify(filters, { arrayFormat: 'bracket', skipEmptyString: true, skipNull: true })
    const url = `${window.location.origin}${window.location.pathname}${urlParams ? '?' + urlParams : ''}`
    history.replaceState({}, document.title, url)
    this.loadEvents()
  }

  updateFilters = transform => {
    this.setState(
      ({ filters }) => ({
        filters: { ...filters, ...transform(filters) },
      }),
      () => this.updateUrl()
    )
  }

  toggleParamValue = (key, value) => {
    const config = PARAM_CONFIG[key]
    if (config.multiple) {
      this.updateFilters(filters => ({
        [key]: filters[key].includes(value) ? filters[key].filter(v => v !== value) : filters[key].concat(value),
      }))
    } else {
      this.updateFilters(filters => ({
        [key]: filters[key] === value ? config.default : value,
      }))
    }
  }

  setParams = params => {
    this.updateFilters(() => params)
  }

  clearParams = keys => {
    if (!Array.isArray(keys)) keys = [keys]
    const allDefaults = coerceParamValues({})
    const clearedParams = {}
    for (let key of keys) {
      clearedParams[key] = allDefaults[key]
    }
    this.updateFilters(() => clearedParams)
  }

  clearAllParams = () => {
    this.updateFilters(filters => coerceParamValues({ view: this.props.showView ? filters.view : undefined }))
  }

  componentDidMount = () => {
    window.addEventListener('updateSearchLocation', event => {
      this.setParams({ lat: event.detail.lat, lng: event.detail.lng })
    })
  }

  render() {
    const { filters, loading, numResults, events, days } = this.state
    return (
      <Fragment>
        <EventsFilters
          {...this.props}
          numResults={numResults}
          filters={filters}
          toggleParamValue={this.toggleParamValue}
          setParams={this.setParams}
          clearParams={this.clearParams}
          clearAllParams={this.clearAllParams}
        />
        {(loading || !numResults) && (
          <section className="section">
            <div className="section-inner padding-vertical-xl justify-content-center">
              <div className="container">
                <div className="flex align-left max-width-container flex-column">
                  <div className="section-header">
                    <div className="flex-0-1-auto max-width-25rem">
                      {loading && (
                        <h2 className="no-margin-bottom font-family-display title-m">
                          <div
                            className="spinner display-inline-block margin-right-xs"
                            style={{ width: 28, height: 28 }}
                          />
                          <span>Loading...</span>
                        </h2>
                      )}
                      {!numResults && (
                        <h2 className="no-margin-bottom font-family-display title-m">
                          <span>No events found that matched your search.</span>
                        </h2>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
        {!loading && numResults > 0 && filters.view === 'schedule' && (
          <section className="section">
            <div className="section-inner padding-vertical-xl justify-content-center">
              <div className="container">
                <EventsSchedule events={events} days={days} />
              </div>
            </div>
          </section>
        )}
      </Fragment>
    )
  }
}

window.EventsIndex = {
  init: (id, props) => {
    ReactDOM.render(<EventsIndex {...props} />, document.getElementById(id))
  },
}
